import { useCallback, useEffect, useRef, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import resourceUrl from 'services/data/resources';
import { KycStatusStreamError, KycStatusStreamResponse } from 'services/types/kycStatus';

type KycStatusStreamState = {
  data?: KycStatusStreamResponse;
  error?: KycStatusStreamError;
  isConnected: boolean;
  isLoading: boolean;
};

const getStreamUrl = () =>
  `${process.env.REACT_APP_DATA_PROVIDER_URL || ''}${resourceUrl('kycStatusStream')}`;

const MAX_AUTH_RECONNECT_RETRIES = 2;
const AUTH_RECONNECT_DELAY_MS = 500;

const useKycStatusStream = () => {
  const [state, setState] = useState<KycStatusStreamState>({
    isConnected: false,
    isLoading: false,
  });

  const controllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const connectRef = useRef<() => void>(() => {});
  const isMountedRef = useRef(true);

  const safeSetState = useCallback(
    (updater: (prev: KycStatusStreamState) => KycStatusStreamState) => {
      if (!isMountedRef.current) return;
      setState((prev) => updater(prev));
    },
    []
  );

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    retryCountRef.current = 0;

    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    safeSetState((prev) => ({
      ...prev,
      isConnected: false,
      isLoading: false,
    }));
  }, [safeSetState]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) return;

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectRef.current();
    }, AUTH_RECONNECT_DELAY_MS);
  }, []);

  const handleError = useCallback(
    (error: unknown) => {
      const normalizedError = (
        error instanceof Error
          ? (error as KycStatusStreamError)
          : (new Error('Failed to connect to verification status stream') as KycStatusStreamError)
      ) as KycStatusStreamError;

      if (
        normalizedError.status === undefined &&
        typeof (error as { status?: number })?.status === 'number'
      ) {
        normalizedError.status = (error as { status: number }).status;
      }

      controllerRef.current = null;

      if (normalizedError.status === 307) {
        if (retryCountRef.current >= MAX_AUTH_RECONNECT_RETRIES) {
          const retryError = new Error(
            'Unable to reconnect to verification status. Please try again.'
          ) as KycStatusStreamError;

          safeSetState((prev) => ({
            ...prev,
            error: retryError,
            isConnected: false,
            isLoading: false,
          }));
          return;
        }

        retryCountRef.current += 1;

        safeSetState((prev) => ({
          ...prev,
          data: undefined,
          error: undefined,
          isConnected: false,
          isLoading: true,
        }));

        scheduleReconnect();
        return;
      }

      safeSetState((prev) => ({
        ...prev,
        error: normalizedError,
        isConnected: false,
        isLoading: false,
      }));
    },
    [safeSetState, scheduleReconnect]
  );

  const connect = useCallback(() => {
    if (controllerRef.current) return;

    safeSetState((prev) => ({
      ...prev,
      isLoading: true,
      error: undefined,
      data: undefined,
    }));

    const controller = new AbortController();
    controllerRef.current = controller;

    Auth.currentSession()
      .then((aws) => {
        const token = aws['idToken'].jwtToken;

        return fetchEventSource(getStreamUrl(), {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          async onopen(response) {
            if (response.ok && response.status >= 200 && response.status < 300) {
              retryCountRef.current = 0;
              if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
              }
              safeSetState((prev) => ({
                ...prev,
                isConnected: true,
                isLoading: false,
              }));
              return;
            }

            const message = await response.text().catch(() => '');
            // Preserve HTTP status so callers can react to redirect/auth errors.
            const streamError = new Error(
              message || 'Unable to open verification status stream.'
            ) as KycStatusStreamError;
            streamError.status = response.status;
            streamError.body = message || undefined;
            throw streamError;
          },
          onmessage(event) {
            if (!event?.data) return;

            let parsedData: KycStatusStreamResponse;

            try {
              parsedData = JSON.parse(event.data);
            } catch (parseError) {
              parsedData = { status: event.data };
            }

            safeSetState((prev) => ({
              ...prev,
              data: parsedData,
            }));
          },
          onclose() {
            controllerRef.current = null;
            safeSetState((prev) => ({
              ...prev,
              isConnected: false,
              isLoading: false,
            }));
          },
          onerror(error) {
            handleError(error);
            throw error;
          },
        });
      })
      .catch((error) => {
        handleError(error);
      });
  }, [handleError, safeSetState]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  return {
    data: state.data,
    error: state.error,
    isConnected: state.isConnected,
    isLoading: state.isLoading,
    connect,
    disconnect,
  };
};

export default useKycStatusStream;
